import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Trophy, Plus, Pencil, Trash2, X, Crown, Star, Flame, Award,
  Coins, Sparkles, Clock, Gift, Users, ChevronDown, ChevronUp, PlusCircle, ArrowRight,
} from "lucide-react";

const METRICS = [
  { key: "manual", label: "Manual (I add entries)", icon: Pencil, help: "You add and edit rows yourself." },
  { key: "wins", label: "Game wins in my channel", icon: Trophy, help: "Auto-tracked. Optionally filter by a game." },
  { key: "tips_gc", label: "Gold tipped to me", icon: Coins, help: "Top tippers of this channel." },
  { key: "sparks", label: "Total Sparks (global)", icon: Sparkles, help: "Ranks by all-time Sparks earned." },
  { key: "watch_time", label: "Watch minutes in my channel", icon: Clock, help: "Loyal viewer minutes." },
  { key: "gifts_given", label: "Gifts given to my channel", icon: Gift, help: "Top gift senders." },
];

const ICONS = { Trophy, Crown, Star, Flame, Award, Coins, Sparkles, Clock, Gift, Users };

export default function ChannelLeaderboard({ channelUsername }) {
  const { user } = useAuth();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const isOwner = user && (user.username === channelUsername || user.is_admin);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/channels/${channelUsername}/leaderboards`);
      // Show only on-site boards in the sidebar widget — external ones live on the full page
      setBoards((r.data || []).filter(b => (b.kind || "internal") !== "external"));
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [channelUsername]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-[#18181B] border border-white/10 rounded-md overflow-hidden" data-testid="channel-leaderboards">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[color:var(--sc-volt)]" />
          <div className="sc-label">Leaderboards</div>
        </div>
        {isOwner && (
          <button
            onClick={() => setCreating(true)}
            className="sc-btn text-[10px] flex items-center gap-1 py-1 px-2"
            data-testid="create-leaderboard-btn"
          >
            <PlusCircle className="w-3 h-3" /> New
          </button>
        )}
      </div>

      <div className="divide-y divide-white/5">
        {loading && <div className="p-4 text-xs text-zinc-500 sc-mono">loading…</div>}
        {!loading && boards.length === 0 && (
          <div className="p-6 text-center">
            <Trophy className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
            <div className="text-xs text-zinc-500">
              {isOwner ? "No leaderboards yet. Create one to spotlight your community." : "This channel hasn't created a leaderboard yet."}
            </div>
          </div>
        )}
        {boards.map((b) => (
          <BoardCard
            key={b.id}
            board={b}
            channelUsername={channelUsername}
            isOwner={isOwner}
            onEdit={() => setEditingBoard(b)}
            onReload={load}
          />
        ))}
      </div>

      {(creating || editingBoard) && (
        <BoardEditor
          initial={editingBoard || null}
          onClose={() => { setCreating(false); setEditingBoard(null); }}
          onSaved={() => { setCreating(false); setEditingBoard(null); load(); }}
        />
      )}

      <Link
        to={`/c/${channelUsername}/leaderboards`}
        className="w-full flex items-center justify-center gap-1.5 text-[10px] sc-mono uppercase tracking-widest text-zinc-500 hover:text-[color:var(--sc-volt)] transition p-3 border-t border-white/5"
        data-testid="sidebar-see-all-leaderboards"
      >
        See full leaderboards page <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

// ============================================================
function BoardCard({ board, channelUsername, isOwner, onEdit, onReload }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [addingEntry, setAddingEntry] = useState(false);
  const Icon = ICONS[board.icon] || Trophy;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/channels/${channelUsername}/leaderboards/${board.id}`);
      setEntries(r.data.entries || []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [channelUsername, board.id]);
  useEffect(() => { load(); }, [load]);

  const deleteBoard = async () => {
    if (!confirm(`Delete "${board.title}"? All entries will be lost.`)) return;
    try {
      await api.delete(`/channels/me/leaderboards/${board.id}`);
      toast.success("Leaderboard deleted");
      onReload();
    } catch { toast.error("Failed"); }
  };

  const removeEntry = async (username) => {
    if (!confirm(`Remove @${username}?`)) return;
    try {
      await api.delete(`/channels/me/leaderboards/${board.id}/entries/${encodeURIComponent(username)}`);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-3" data-testid={`board-${board.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 text-left">
        <Icon className="w-4 h-4 text-[color:var(--sc-volt)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="sc-heading font-bold text-sm truncate">{board.title}</div>
          {board.description && <div className="text-[10px] text-zinc-500 truncate">{board.description}</div>}
        </div>
        {board.pinned && <span className="chip-neon volt text-[9px]">PINNED</span>}
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-1">
          {loading && <div className="text-[10px] text-zinc-500 sc-mono">loading…</div>}
          {!loading && entries.length === 0 && (
            <div className="text-[11px] text-zinc-500 py-2">
              {board.metric === "manual"
                ? isOwner ? "No entries yet. Add the first one below." : "No entries yet."
                : "No data yet — activity will populate this."}
            </div>
          )}
          {entries.map((e, i) => (
            <div key={`${e.username}-${i}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`entry-${e.username}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={`sc-mono text-[10px] w-5 text-right flex-shrink-0 ${
                  i === 0 ? "text-[color:var(--sc-volt)]" : i === 1 ? "text-[color:var(--sc-cyan)]" : i === 2 ? "text-[color:var(--sc-orange)]" : "text-zinc-600"
                }`}>
                  {i + 1}
                </span>
                <span className="text-xs text-white truncate">@{e.username}</span>
                {e.notes && <span className="text-[10px] text-zinc-500 truncate">· {e.notes}</span>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="sc-mono text-xs font-bold text-[color:var(--sc-volt)]">
                  {typeof e.value === "number" ? e.value.toLocaleString() : e.value}
                </span>
                <span className="text-[9px] text-zinc-500">{board.unit_label}</span>
                {isOwner && board.metric === "manual" && (
                  <button onClick={() => removeEntry(e.username)} className="text-zinc-500 hover:text-red-400" data-testid={`remove-entry-${e.username}`} aria-label="Remove">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {isOwner && board.metric === "manual" && (
            addingEntry
              ? <ManualEntryForm boardId={board.id} onDone={() => { setAddingEntry(false); load(); }} onCancel={() => setAddingEntry(false)} unitLabel={board.unit_label} />
              : <button onClick={() => setAddingEntry(true)} className="sc-btn-ghost text-[10px] mt-1 flex items-center gap-1" data-testid={`add-entry-${board.id}`}>
                  <Plus className="w-3 h-3" /> Add entry
                </button>
          )}

          {isOwner && (
            <div className="flex items-center gap-2 pt-2 mt-2 border-t border-white/5">
              <button onClick={onEdit} className="sc-btn-ghost text-[10px] flex items-center gap-1" data-testid={`edit-board-${board.id}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
              <button onClick={deleteBoard} className="sc-btn-ghost text-[10px] flex items-center gap-1 text-[color:var(--sc-pink)]" data-testid={`delete-board-${board.id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
function ManualEntryForm({ boardId, onDone, onCancel, unitLabel }) {
  const [username, setUsername] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !value) return toast.error("Username + value required");
    setBusy(true);
    try {
      await api.post(`/channels/me/leaderboards/${boardId}/entries`, {
        username: username.trim(), value: parseFloat(value), notes,
      });
      toast.success("Entry saved");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-[1fr_80px_auto] gap-1 mt-2 items-center" data-testid="manual-entry-form">
      <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" className="sc-input py-1 text-xs" required data-testid="entry-username" />
      <input value={value} onChange={(e) => setValue(e.target.value)} type="number" step="any" placeholder={unitLabel} className="sc-input py-1 text-xs" required data-testid="entry-value" />
      <div className="flex gap-1">
        <button type="submit" disabled={busy} className="sc-btn text-[10px] py-1 px-2" data-testid="entry-save">Save</button>
        <button type="button" onClick={onCancel} className="sc-btn-ghost text-[10px] py-1 px-2">✕</button>
      </div>
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="notes (optional)" className="sc-input py-1 text-xs col-span-3" data-testid="entry-notes" />
    </form>
  );
}

// ============================================================
function BoardEditor({ initial, onClose, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [metric, setMetric] = useState(initial?.metric || "manual");
  const [unitLabel, setUnitLabel] = useState(initial?.unit_label || "pts");
  const [icon, setIcon] = useState(initial?.icon || "Trophy");
  const [sortOrder, setSortOrder] = useState(initial?.sort_order || "desc");
  const [gameSlug, setGameSlug] = useState(initial?.game_slug || "");
  const [limit, setLimit] = useState(initial?.limit || 25);
  const [pinned, setPinned] = useState(!!initial?.pinned);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        metric, unit_label: unitLabel, icon, sort_order: sortOrder,
        game_slug: gameSlug.trim() || null,
        limit: Math.max(3, Math.min(100, parseInt(limit, 10) || 25)),
        pinned,
      };
      if (initial) {
        await api.patch(`/channels/me/leaderboards/${initial.id}`, payload);
      } else {
        await api.post("/channels/me/leaderboards", payload);
      }
      toast.success(initial ? "Updated" : "Leaderboard created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(20px)" }} onClick={onClose}>
      <form onSubmit={save} className="sc-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="board-editor">
        <div className="flex items-center justify-between mb-4">
          <div className="sc-heading text-lg font-black">{initial ? "Edit Leaderboard" : "New Leaderboard"}</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="sc-label block mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} className="sc-input text-sm" required data-testid="board-title" />
          </div>
          <div>
            <label className="sc-label block mb-1">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} className="sc-input text-sm" data-testid="board-description" />
          </div>
          <div>
            <label className="sc-label block mb-1">How is this ranked?</label>
            <div className="space-y-1">
              {METRICS.map((m) => (
                <label key={m.key} className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition ${
                  metric === m.key ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/10" : "border-white/10 hover:border-white/30"
                }`} data-testid={`metric-${m.key}`}>
                  <input type="radio" name="metric" value={m.key} checked={metric === m.key} onChange={() => setMetric(m.key)} className="mt-0.5" />
                  <m.icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--sc-volt)" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-white">{m.label}</div>
                    <div className="text-[10px] text-zinc-500 leading-relaxed">{m.help}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {metric === "wins" && (
            <div>
              <label className="sc-label block mb-1">Filter by game (optional)</label>
              <input value={gameSlug} onChange={(e) => setGameSlug(e.target.value)} placeholder="e.g. chess, holdem, slots" className="sc-input text-sm" data-testid="board-game-slug" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="sc-label block mb-1">Unit label</label>
              <input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} maxLength={20} className="sc-input text-sm" data-testid="board-unit" />
            </div>
            <div>
              <label className="sc-label block mb-1">Show top…</label>
              <input value={limit} onChange={(e) => setLimit(e.target.value)} type="number" min={3} max={100} className="sc-input text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="sc-label block mb-1">Icon</label>
              <select value={icon} onChange={(e) => setIcon(e.target.value)} className="sc-input text-sm" data-testid="board-icon">
                {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="sc-label block mb-1">Sort</label>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="sc-input text-sm">
                <option value="desc">Highest first</option>
                <option value="asc">Lowest first</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="board-pinned" />
            Pin to top of leaderboards list
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="sc-btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="sc-btn text-xs" data-testid="board-save">
            {busy ? "Saving…" : initial ? "Save changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
